import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { MerchantSettings as SettingsType } from "../../domain/merchant";
import { getMerchantSettings, saveMerchantSettings } from "../../services/api/merchant";
import { normalizeError } from "../../services/http/errors";

const COLOR_OPTIONS = [
  { value: "#4f46e5", label: "Indigo" },
  { value: "#2563eb", label: "Blue" },
  { value: "#0f172a", label: "Black" },
  { value: "#16a34a", label: "Green" },
  { value: "#f97316", label: "Orange" },
];

export default function MerchantSettings() {
  const { tenantSlug = "" } = useParams();
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success" as "success" | "error", msg: "" });

  useEffect(() => {
    getMerchantSettings(tenantSlug).then(setSettings).catch(() => setSettings(null));
  }, [tenantSlug]);

  const base = process.env.PUBLIC_URL || "";
  const portalUrl = `${window.location.origin}${base}/r/${tenantSlug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setToast({ open: true, severity: "success", msg: "Copied to clipboard." });
    } catch {
      setToast({ open: true, severity: "error", msg: "Copy failed." });
    }
  };

  const embedSnippet = `<a href="${portalUrl}" style="display:inline-block;padding:12px 24px;background:${settings?.branding.primaryColor || "#2563eb"};color:#fff;border-radius:8px;text-decoration:none;font-family:sans-serif;font-weight:600">Return or Exchange</a>`;

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setToast({ open: true, severity: "success", msg: "Snippet copied." });
    } catch {
      setToast({ open: true, severity: "error", msg: "Copy failed." });
    }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await saveMerchantSettings(tenantSlug, settings);
      setToast({ open: true, severity: "success", msg: "Published successfully." });
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ mr: 2 }} />
        <Typography color="text.secondary" fontWeight={600}>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card sx={{ width: "100%", maxWidth: 900 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "primary.light", color: "primary.main" }}>
              <PublicOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Portal Branding</Typography>
              <Typography variant="caption" color="text.secondary">How your customers experience the return flow</Typography>
            </Box>
          </Stack>

          <CardContent>
            <Stack spacing={3}>
              {/* Portal URL */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Public Portal Endpoint
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={portalUrl}
                    InputProps={{ readOnly: true }}
                    sx={{ "& .MuiInputBase-input": { fontWeight: 600, color: "text.primary" } }}
                  />
                  <IconButton onClick={copy} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              {/* Embed Snippet */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Embed on Your Store
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                  Paste this into your Salla store footer, custom page, or thank-you email
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={embedSnippet}
                    InputProps={{ readOnly: true }}
                    sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: 12 } }}
                  />
                  <IconButton onClick={copySnippet} sx={{ border: 1, borderColor: "divider", borderRadius: 2, mt: 0.5 }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: "grey.50", border: 1, borderColor: "divider", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Preview</Typography>
                  <a
                    href={portalUrl}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: "inline-block",
                      padding: "12px 24px",
                      background: settings.branding.primaryColor || "#2563eb",
                      color: "#fff",
                      borderRadius: 8,
                      textDecoration: "none",
                      fontFamily: "sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Return or Exchange
                  </a>
                </Box>
              </Box>

              {/* Color + Support Email */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Primary Color
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {COLOR_OPTIONS.map((c) => (
                      <Box
                        key={c.value}
                        onClick={() => setSettings({ ...settings, branding: { ...settings.branding, primaryColor: c.value } })}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          bgcolor: c.value,
                          cursor: "pointer",
                          border: 2,
                          borderColor: settings.branding.primaryColor === c.value ? "primary.main" : "transparent",
                          outline: settings.branding.primaryColor === c.value ? "2px solid" : "none",
                          outlineColor: "primary.light",
                          outlineOffset: 2,
                          transition: "all 0.15s",
                        }}
                        aria-label={c.label}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Support Email
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="support@example.com"
                    value={settings.portal.supportEmail || ""}
                    onChange={(e) => setSettings({ ...settings, portal: { ...settings.portal, supportEmail: e.target.value || undefined } })}
                  />
                </Grid>
              </Grid>

              {/* Policy URL */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Return Policy URL
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="https://example.com/return-policy"
                  value={settings.portal.policyUrl || ""}
                  onChange={(e) => setSettings({ ...settings, portal: { ...settings.portal, policyUrl: e.target.value || undefined } })}
                />
              </Box>

              <Button variant="contained" size="large" fullWidth onClick={save} disabled={saving}>
                {saving ? "Publishing..." : "Publish Portal Config"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
